import React, { useState, useEffect, useRef } from "react";
import {
    Facebook,
    Twitter,
    Instagram,
    Linkedin,
    Youtube,
    Music,
    ExternalLink,
    Hash,
    Info,
    Edit,
    Loader2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
    fetchLatestSocialLink,
    fetchSocialLinkByType,
    createSocialLink,
    updateSocialLink
} from "../../redux/Slices/contactInfoSlice";
import { SocialLinkBackendResponse, SocialLinkTypeEnum } from "../../modules/shared/model/contact-info.interfaces";
import SocialMediaModal from "./SocialMediaModal";
import { useLocation, useSearchParams } from "react-router-dom";

interface SocialMediaUrls {
    facebook: string;
    twitter: string;
    instagram: string;
    linkedin: string;
    youtube: string;
    tiktok: string;
}

const SocialMediaManagement: React.FC = () => {
    const dispatch = useAppDispatch();
    const { socialLinks } = useAppSelector((state) => state.contactInfos);
    const searchParams = useLocation();
    // Get type from search params or default to Terms_Conditions
    const typeParam = searchParams.pathname;
    const type = typeParam.includes("/terms") ? SocialLinkTypeEnum.Terms_Conditions : SocialLinkTypeEnum.Help_Center;

    // Default social media URLs
    const defaultUrls: SocialMediaUrls = {
        facebook: "",
        twitter: "",
        instagram: "",
        linkedin: "",
        youtube: "",
        tiktok: ""
    };

    const [showEditModal, setShowEditModal] = useState(false);
    const previousSocialLinksRef = useRef<SocialLinkBackendResponse | null>(null);

    // Fetch social links on component mount
    useEffect(() => {
        if (type && socialLinks.current === null && !socialLinks.loading) {
            dispatch(fetchSocialLinkByType(type));
        }
    }, [dispatch, type, socialLinks.current, socialLinks.loading]);

    // Update ref when Redux state changes
    useEffect(() => {
        if (socialLinks.current && socialLinks.current !== previousSocialLinksRef.current) {
            previousSocialLinksRef.current = socialLinks.current;
        }
    }, [socialLinks.current]);


    // Use Redux data if available, otherwise fall back to default URLs
    const currentUrls = socialLinks.current ? {
        facebook: socialLinks.current.facebook || "",
        twitter: socialLinks.current.twitter || "",
        instagram: socialLinks.current.instagram || "",
        linkedin: socialLinks.current.linkedin || "",
        youtube: socialLinks.current.youtube || "",
        tiktok: socialLinks.current.tiktok || ""
    } : defaultUrls;

    return (
        <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <h2 className="text-lg sm:text-xl font-semibold">Social Media URLs</h2>
                <Button
                    onClick={() => setShowEditModal(true)}
                    className="w-full sm:w-auto text-sm"
                    disabled={socialLinks.loading}
                >
                    {socialLinks.loading ? (
                        <>
                            <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2 animate-spin" />
                            Loading...
                        </>
                    ) : (
                        <>
                            <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                            Edit URLs
                        </>
                    )}
                </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:gap-6">
                <Card className="p-4 sm:p-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Facebook className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            <div className="flex-grow">
                                <div className="font-medium">Facebook</div>
                                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <ExternalLink className="h-3 w-3" />
                                    <a
                                        href={currentUrls.facebook}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline text-blue-600 truncate"
                                    >
                                        {currentUrls.facebook}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Twitter className="h-5 w-5 text-blue-400 flex-shrink-0" />
                            <div className="flex-grow">
                                <div className="font-medium">Twitter</div>
                                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <ExternalLink className="h-3 w-3" />
                                    <a
                                        href={currentUrls.twitter}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline text-blue-600 truncate"
                                    >
                                        {currentUrls.twitter}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Instagram className="h-5 w-5 text-pink-600 flex-shrink-0" />
                            <div className="flex-grow">
                                <div className="font-medium">Instagram</div>
                                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <ExternalLink className="h-3 w-3" />
                                    <a
                                        href={currentUrls.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline text-blue-600 truncate"
                                    >
                                        {currentUrls.instagram}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Linkedin className="h-5 w-5 text-blue-800 flex-shrink-0" />
                            <div className="flex-grow">
                                <div className="font-medium">LinkedIn</div>
                                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <ExternalLink className="h-3 w-3" />
                                    <a
                                        href={currentUrls.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline text-blue-600 truncate"
                                    >
                                        {currentUrls.linkedin}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Youtube className="h-5 w-5 text-red-600 flex-shrink-0" />
                            <div className="flex-grow">
                                <div className="font-medium">YouTube</div>
                                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <ExternalLink className="h-3 w-3" />
                                    <a
                                        href={currentUrls.youtube}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline text-blue-600 truncate"
                                    >
                                        {currentUrls.youtube}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <Music className="h-5 w-5 text-black flex-shrink-0" />
                            <div className="flex-grow">
                                <div className="font-medium">TikTok</div>
                                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <ExternalLink className="h-3 w-3" />
                                    <a
                                        href={currentUrls.tiktok}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline text-blue-600 truncate"
                                    >
                                        {currentUrls.tiktok}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="p-4 sm:p-6">
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium">Usage Information</h3>
                        <div className="p-4 bg-blue-50 rounded-lg">
                            <div className="flex items-start gap-3">
                                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                                <div>
                                    <p className="text-sm text-blue-700 font-medium">How Social Media URLs Are Used</p>
                                    <p className="text-sm text-blue-600 mt-1">
                                        These URLs will be displayed in the website footer, customer dashboard, and professional dashboard.
                                        They allow users to connect with Kokobeo on various social media platforms.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <h4 className="font-medium">Display Locations:</h4>
                            <div className="flex flex-wrap gap-2">
                                <Badge variant={undefined}>Website Footer</Badge>
                                <Badge variant={undefined}>Customer Dashboard</Badge>
                                <Badge variant={undefined}>Professional Dashboard</Badge>
                                <Badge variant={undefined}>Mobile App</Badge>
                            </div>
                        </div>

                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <Hash className="h-4 w-4 text-gray-500" />
                                <span className="text-sm font-medium">Social Media Hashtag</span>
                            </div>
                            <div className="mt-1 text-sm">#Kokobeo</div>
                        </div>
                    </div>
                </Card>
            </div>

            <SocialMediaModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
            />
        </div>
    );
};

export default SocialMediaManagement;
